// Hero/class gameplay stats, ported from prototypes/match/js/sim.js (HEROES/CLASSES/CLASS_BASE/PAIRS).
//
// Fields marked view-only below (model, fallback, scale, builder, bolt, boltLook, boltSpeed's *look*,
// clips) are kept here for now per the porting brief rather than split into a separate client table.
// Core reads only `clips` (an opaque list of animation-clip names used to alternate attack swings and
// as a fallback-preference list handed to anim events) and `boltSpeed`/`dmg`/etc (real gameplay numbers).
// Core never branches on whether a named clip exists on a model — that decision belongs to the view.

export type Role = 'guard' | 'shooter' | 'duelist' | 'support' | 'gamechanger' | 'summoner';
export type ClassId = 'knight' | 'alchemist' | 'reaper' | 'doctor' | 'outsider' | 'summoner';

export type HeroDef = {
  cls: ClassId;
  role: Role;
  hp: number;
  speed: number;
  range: number;
  attackCd: number;
  dmg: number;
  impact: number;
  turn: number;
  clips: string[];       // view-only: attack-swing clip names, alternated by clipFlip
  melee?: boolean;
  builder?: string;      // view-only
  bolt?: number;         // view-only
  boltLook?: string;     // view-only
  boltSpeed?: number;
  scale?: number;        // view-only
  model?: string;        // view-only
  fallback?: string;     // view-only
};

const CLASS_BASE: Record<ClassId, HeroDef> = {
  knight: { cls: 'knight', role: 'guard', hp: 260, speed: 4.6, range: 1.9, attackCd: 0.95, dmg: 16, impact: 8 / 24, melee: true, turn: 6, clips: ['Attack1', 'Attack2'] },
  alchemist: { cls: 'alchemist', role: 'shooter', hp: 150, speed: 5.0, range: 8, attackCd: 1.3, turn: 8, dmg: 13, impact: 6 / 24, builder: 'alchemist',
               bolt: 0xffb347, boltLook: 'bullet', boltSpeed: 38, clips: ['Attack'], fallback: 'assets/alchemist_v1.gltf.json' },
  reaper: { cls: 'reaper', role: 'duelist', hp: 180, speed: 5.4, range: 1.8, attackCd: 0.8, turn: 9.5, dmg: 13, impact: 7 / 24, melee: true, builder: 'reaper',
            clips: ['Attack1', 'Attack2'], fallback: 'assets/reaper_v1.gltf.json' },
  doctor: { cls: 'doctor', role: 'support', hp: 170, speed: 4.9, range: 7, attackCd: 1.5, turn: 7, dmg: 11, impact: 0.4, scale: 1.08,
            bolt: 0x9fe07a, boltLook: 'smoke', boltSpeed: 11, clips: ['Attack'], fallback: 'assets/plague_doctor_v1.gltf.json' },
  outsider: { cls: 'outsider', role: 'gamechanger', hp: 150, speed: 5.2, range: 5, attackCd: 1.3, turn: 8, dmg: 13, impact: 6 / 24,
              bolt: 0x9a8cc8, boltLook: 'mote', boltSpeed: 20, clips: ['Attack'], fallback: 'assets/plague_doctor_v1.gltf.json' },
  summoner: { cls: 'summoner', role: 'summoner', hp: 145, speed: 5.0, range: 6, attackCd: 1.4, turn: 8, dmg: 11, impact: 6 / 24,
              bolt: 0xb89a6a, boltLook: 'mote', boltSpeed: 18, clips: ['Attack'], fallback: 'assets/plague_doctor_v1.gltf.json' },
};
export const CLASSES: Record<ClassId, { chars: string[] }> = {
  knight: { chars: ['rhino', 'durer', 'landsknecht'] },
  alchemist: { chars: ['arquebusier', 'astrologer', 'salamander'] },
  reaper: { chars: ['death', 'ferryman', 'devil'] },
  doctor: { chars: ['plague', 'monk', 'grotesque'] },
  outsider: { chars: ['shadow', 'sin', 'nightmare'] },
  summoner: { chars: ['birdcatcher', 'puppeteer', 'shewolf'] },
};
const char = (cls: ClassId, o: Partial<HeroDef>): HeroDef => ({ ...CLASS_BASE[cls], ...o });

export const HEROES = {
  rhino: char('knight', { hp: 270, model: 'assets/knight_rhino.gltf.json' }),
  durer: char('knight', { hp: 250, range: 2.1, model: 'assets/knight_classic.gltf.json' }),
  landsknecht: char('knight', { hp: 240, speed: 4.9, dmg: 18, model: 'assets/knight_landsknecht.gltf.json', fallback: 'assets/knight_classic.gltf.json' }),
  arquebusier: char('alchemist', { hp: 145, model: 'assets/alchemist_v1.gltf.json' }),
  astrologer: char('alchemist', { range: 9, dmg: 12, bolt: 0xb8c8ff, boltLook: 'star', boltSpeed: 24, model: 'assets/alchemist_v2.gltf.json' }),
  salamander: char('alchemist', { hp: 165, range: 6.5, bolt: 0xff8a3c, boltLook: 'ember', boltSpeed: 20, model: 'assets/alchemist_v3.gltf.json' }),
  death: char('reaper', { model: 'assets/reaper_v1.gltf.json' }),
  ferryman: char('reaper', { hp: 190, speed: 5.2, range: 2.2, model: 'assets/reaper_v2.gltf.json' }),
  devil: char('reaper', { hp: 180, speed: 5.6, model: 'assets/reaper_v3.gltf.json' }),
  plague: char('doctor', { model: 'assets/plague_doctor_v1.gltf.json' }),
  monk: char('doctor', { hp: 180, dmg: 9, bolt: 0xf6e8a0, boltLook: 'mote', boltSpeed: 16, model: 'assets/plague_doctor_v2.gltf.json' }),
  grotesque: char('doctor', { hp: 180, dmg: 13, bolt: 0xb4c07a, boltLook: 'spit', boltSpeed: 14, scale: 1.0, model: 'assets/plague_doctor_v3.gltf.json' }),
  shadow: char('outsider', { dmg: 15, bolt: 0x6a6a8a, boltLook: 'sliver', model: 'assets/astral_shadow.gltf.json' }),
  sin: char('outsider', { hp: 165, bolt: 0xc0506a, boltLook: 'drop', model: 'assets/astral_sin.gltf.json' }),
  nightmare: char('outsider', { hp: 165, bolt: 0x8a7ab8, boltLook: 'crow', model: 'assets/astral_nightmare.gltf.json' }),
  birdcatcher: char('summoner', { bolt: 0xc8a878, boltLook: 'dart', model: 'assets/summoner_birdcatcher.gltf.json' }),
  puppeteer: char('summoner', { bolt: 0xb07a5a, boltLook: 'bobbin', model: 'assets/summoner_puppeteer.gltf.json' }),
  shewolf: char('summoner', { bolt: 0x9aa0a8, boltLook: 'claw', model: 'assets/summoner_shewolf.gltf.json' }),
} satisfies Record<string, HeroDef>;

export type HeroKind = keyof typeof HEROES;

/* Who fights whom when a player picks a character: the picked class's partner class completes the team.
   «Астрал» and «Заклинатели» have no partner class — a local match seats them with a random member of a random pair. */
export const PAIRS: [ClassId, ClassId][] = [['knight', 'alchemist'], ['reaper', 'doctor']];
